/**
 * Calendly popup integration.
 *
 * Intercepts clicks on any link pointing to calendly.com (e.g. the "Book a
 * meeting" core/button used across the cta, hero and media-text-cta blocks)
 * and opens Calendly's official popup widget instead of navigating away.
 *
 * Also listens for Calendly's `event_scheduled` postMessage and forwards it
 * to window.dataLayer so a GTM trigger can turn it into a GA4 conversion
 * event once GTM is added to the site.
 */

const CALENDLY_SCRIPT_URL = 'https://assets.calendly.com/assets/external/widget.js';
const CALENDLY_STYLE_URL  = 'https://assets.calendly.com/assets/external/widget.css';
const CALENDLY_LINK_SELECTOR = 'a[href*="calendly.com"]';

// Mobile breakpoint mirrors the theme's `md` SCSS breakpoint.
const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

let widgetLoadingPromise = null;

/**
 * Lazily injects the Calendly widget script/stylesheet, only once, and only
 * when a Calendly link actually exists on the page.
 *
 * @return {Promise<void>} Resolves once the widget script is ready to use.
 */
const loadCalendlyWidget = () => {

	if ( widgetLoadingPromise ) {

		return widgetLoadingPromise;

	}

	widgetLoadingPromise = new Promise( ( resolve, reject ) => {

		if ( window.Calendly ) {

			resolve();
			return;

		}

		const link = document.createElement( 'link' );
		link.rel  = 'stylesheet';
		link.href = CALENDLY_STYLE_URL;
		document.head.appendChild( link );

		const script  = document.createElement( 'script' );
		script.src    = CALENDLY_SCRIPT_URL;
		script.async  = true;
		script.onload  = () => resolve();
		script.onerror = () => reject( new Error( 'Failed to load Calendly widget script.' ) );
		document.head.appendChild( script );

	} );

	return widgetLoadingPromise;

};

/**
 * Resolves the Calendly URL to open for the current viewport, swapping the
 * desktop link for the mobile-specific one when needed.
 *
 * Both links are managed globally in Settings → Calendly (see
 * `Wombat\CalendlySettings`) and passed to the frontend via `themeVars`.
 * When a mobile link isn't configured, the original href is used on all
 * viewports.
 *
 * @param {string} href Original href from the clicked link.
 * @return {string} URL to pass to Calendly.initPopupWidget.
 */
const resolveCalendlyUrl = ( href ) => {

	const mobileUrl = window.themeVars?.calendlyMobileUrl;

	if ( mobileUrl && window.matchMedia( MOBILE_MEDIA_QUERY ).matches ) {

		return mobileUrl;

	}

	return href;

};

/**
 * Handles clicks on Calendly links via event delegation, opening the popup
 * widget instead of navigating to Calendly's website.
 *
 * @param {MouseEvent} event Click event.
 */
const handleCalendlyLinkClick = ( event ) => {

	const link = event.target.closest( CALENDLY_LINK_SELECTOR );

	if ( ! link ) {

		return;

	}

	event.preventDefault();

	loadCalendlyWidget()
		.then( () => {

			window.Calendly?.initPopupWidget( { url: resolveCalendlyUrl( link.href ) } );

		} )
		.catch( ( error ) => {

			// Fall back to a normal navigation if the widget failed to load.
			// eslint-disable-next-line no-console
			console.error( error );
			window.location.href = link.href;

		} );

};

/**
 * Forwards Calendly's `event_scheduled` postMessage to the dataLayer so a
 * GTM trigger can report it to GA4 as a conversion.
 *
 * @param {MessageEvent} event postMessage event.
 */
const handleCalendlyMessage = ( event ) => {

	if ( event.origin !== 'https://calendly.com' ) {

		return;

	}

	if ( event.data?.event !== 'calendly.event_scheduled' ) {

		return;

	}

	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push( { event: 'calendly_booking_completed' } );

};

/**
 * Initialises the Calendly popup integration, bailing out early when there
 * is no Calendly link on the current page.
 */
export const initCalendlyPopup = () => {

	if ( ! document.querySelector( CALENDLY_LINK_SELECTOR ) ) {

		return;

	}

	document.addEventListener( 'click', handleCalendlyLinkClick );
	window.addEventListener( 'message', handleCalendlyMessage );

};
